// src/components/BusinessCardFlow/BusinessCardsPage.js

import React, { useState } from 'react';
import LandingPage from './components/LandingPage';
import CardCapture from './components/CardCapture';
import CardList from './components/CardList';
import ContactReview from './components/ContactReview';
import FinalScreen from './components/FinalScreen';

export default function BusinessCardsPage() {
  const [stage, setStage] = useState('landing');
  const [capturedCards, setCapturedCards] = useState([]);
  const [extractedContacts, setExtractedContacts] = useState([]);

  // Generate a unique sessionId for the user's session
  const [sessionId] = useState(() => {
    const randPart = Math.random().toString(36).substring(2, 7);
    return Date.now().toString() + '_' + randPart;
  });

  // Loading states
  const [extracting, setExtracting] = useState(false);

  const startProcess = () => setStage('capture');

  // Remove a card from the list by index
  const removeCard = (index) => {
    setCapturedCards((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * 1) /api/business-cards/extract
   * Called by "Process All"
   */
  const handleProcessAll = async () => {
    if (!capturedCards.length) {
      alert('No cards to process!');
      return;
    }
    try {
      setExtracting(true);
      const resp = await fetch('/api/business-cards/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: capturedCards }), // each { imageBase64 }
      });
      const data = await resp.json();
      if (data.contacts) {
        setExtractedContacts(data.contacts);
        setStage('review');
      } else {
        alert(data.error || 'Error extracting business cards');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      alert('Failed to process business cards.');
    } finally {
      setExtracting(false);
    }
  };

  /**
   * 2) /api/business-cards/submit
   * Called by "Approve & Download"
   */
  const handleSubmitAll = async () => {
    if (!extractedContacts.length) {
      alert('No extracted data to submit.');
      return;
    }
    try {
      await fetch('/api/business-cards/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, contacts: extractedContacts }),
      });
      setStage('final');
    } catch (error) {
      console.error(error);
      alert('Error submitting contacts.');
    }
  };

  // Start new batch from final
  const startNewBatch = () => {
    setCapturedCards([]);
    setExtractedContacts([]);
    setStage('landing');
  };

  // Inline container style
  const containerStyle = {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#000',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  };

  // Render stage
  return (
    <div style={containerStyle}>
      {stage === 'landing' && <LandingPage startProcess={startProcess} />}

      {stage === 'capture' && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <CardCapture
            onCardsAdded={(newCards) => {
              // newCards is an array of { imageBase64 }
              setCapturedCards((prev) => [...prev, ...newCards]);
            }}
          />
          {capturedCards.length > 0 && (
            <CardList
              cards={capturedCards}
              removeCard={removeCard}
              processAll={handleProcessAll}
              extracting={extracting}
            />
          )}
        </div>
      )}

      {stage === 'review' && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '1rem'
          }}
        >
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '1rem 0' }}>
            Extracted Data
          </h2>
          {extractedContacts.map((contact, idx) => (
            <ContactReview
              key={idx}
              data={contact}
              update={(updated) => {
                setExtractedContacts((prev) =>
                  prev.map((c, i) => (i === idx ? updated : c))
                );
              }}
            />
          ))}
          <button
            style={{
              marginTop: '1rem',
              padding: '0.75rem 2rem',
              backgroundColor: '#22c55e',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer'
            }}
            onClick={handleSubmitAll}
          >
            Approve & Download
          </button>
        </div>
      )}

      {stage === 'final' && (
        <FinalScreen startNewBatch={startNewBatch} sessionId={sessionId} />
      )}
    </div>
  );
}
